import React, { useState, useRef } from 'react';
import { Text, Icon, MoreActions, Tooltip } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { IMoreActionItem } from '@ui-library/MoreActions/MoreActions.types';
import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react';
import type { Artifact, RequirementGroup } from '../RequirementGroups.types';
import { ArtifactItem } from './ArtifactItem';
import { IfHasPermission } from '../../IfHasPermission';
import { Permissions } from '../../../constants';

interface GroupItemProps {
  group: RequirementGroup;
  isExpanded: boolean;
  isActiveThread: boolean;
  artifacts: Artifact[];
  permissions: string[];
  onToggle: () => void;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onArtifactSelect: (artifact: Artifact) => void;
  onArtifactOpenInNewTab: (artifact: Artifact) => void;
  onArtifactDownload: (identifier: string) => void;
  onArtifactRename: (artifact: Artifact) => void;
  onArtifactDelete: (artifact: Artifact) => void;
  deletingArtifactId: string | null;
  isArtifactDownloading: (id: string) => boolean;
  handleCloseArtifactPreview: () => void;
  handleShareArtifact: (artifact: Artifact) => void;
}

export const GroupItem: React.FC<GroupItemProps> = ({
  group,
  isExpanded,
  isActiveThread,
  artifacts,
  permissions,
  onToggle,
  onSelect,
  onEdit,
  onDelete,
  onArtifactSelect,
  onArtifactOpenInNewTab,
  onArtifactDownload,
  onArtifactRename,
  onArtifactDelete,
  deletingArtifactId,
  isArtifactDownloading,
  handleCloseArtifactPreview,
  handleShareArtifact,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  const menuItems: IMoreActionItem[] = [
    {
      id: 'edit',
      label: 'Edit',
      onClick: () => {
        onEdit();
        setMenuOpen(false);
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      onClick: () => {
        onDelete();
        setMenuOpen(false);
      },
    }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Group Header */}
      <div
        className={`flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm rounded-bg-v2-sm cursor-pointer transition-colors duration-200 relative group ${
          isActiveThread ? 'bg-bg-quaternary' : 'hover:bg-bg-secondary'
        }`}
        onClick={() => {
          onSelect();
          handleCloseArtifactPreview();
          if (!isExpanded) {
            onToggle();
          }
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Chevron Icon */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="flex items-center justify-center w-4 h-4 bg-transparent border-none cursor-pointer p-0 flex-shrink-0 text-text-tertiary hover:text-text-secondary transition-colors duration-200"
        >
          {isExpanded ? (
            <ChevronDown size={16} className="text-fg-tertiary transition-transform duration-300" />
          ) : (
            <ChevronRight size={16} className="text-fg-tertiary transition-transform duration-300" />
          )}
        </button>

        {/* Folder Icon - Changes based on expanded state */}
        <div className="flex items-center justify-center flex-shrink-0">
          {isExpanded ? (
            <FolderOpen size={16} className="text-fg-secondary" />
          ) : (
            <Folder size={16} className="text-fg-secondary" />
          )}
        </div>

        {/* Folder Name and Count */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <Tooltip content={`${group.name} (${group.artifact_count})`} position="right">
            <Text
              variant="text-sm" 
              weight="semibold" 
              color="secondary" 
              className="line-clamp-2 break-words leading-[1.4]"
            >
              {group.name} ({group.artifact_count})
            </Text>
          </Tooltip>
        </div>

        {/* More Actions Menu */}
        <IfHasPermission
          permissions={permissions}
          requires={Permissions.UpdateProjectPermission}
        >
          <div className="relative flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              ref={menuRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="flex items-center justify-center w-6 h-6 rounded-bg-v2-md bg-transparent border-none cursor-pointer text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary transition-all duration-200"
              aria-label="More options"
            >
              <Icon name={IconName.More} size="sm" variant="secondary" />
            </button>

            <MoreActions
              items={menuItems}
              isOpen={menuOpen}
              onClose={() => setMenuOpen(false)}
              position={"bottom-right"}
              triggerRef={menuRef}
            />
          </div>
        </IfHasPermission>
      </div>

      {/* Artifacts List */}
      {isExpanded && (
        <div className="flex flex-col animate-fade-in-up-smooth origin-top" style={{ paddingLeft: '30px' }}>
          {artifacts.length === 0 ? (
            <div className="flex items-center justify-center py-bg-v2-sm px-bg-v2-md text-text-tertiary w-full">
              <Text variant="text-sm" color="tertiary" align="center">No artifacts yet</Text>
            </div>
          ) : (
            artifacts.map(artifact => (
              <ArtifactItem
                key={artifact.id}
                artifact={artifact}
                onSelect={() => onArtifactSelect(artifact)}
                onOpenInNewTab={() => onArtifactOpenInNewTab(artifact)}
                onDownload={() => onArtifactDownload(artifact.identifier)}
                onRename={() => onArtifactRename(artifact)}
                onDelete={() => onArtifactDelete(artifact)}
                isDeleting={deletingArtifactId === artifact.id}
                isDownloading={isArtifactDownloading(artifact.identifier)}
                permissions={permissions}
                handleShareArtifact={handleShareArtifact}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

